/* eslint-disable no-console */
import './style/App.scss';
import { AuthState, OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { LoginCallback, SecureRoute, Security } from '@okta/okta-react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import config from './app/config';

import Header from './components/Header';
import Organization from './pages/organizations/OrganizationDetails';
import OrganizationCreate from './pages/organizations/OrganizationCreate';
import Organizations from './pages/organizations/OrganizationList';
import Profile from './pages/Profile';
import Tenant from './pages/tenants/TenantDetails';
import TenantCreate from './pages/tenants/TenantCreate';
import Tenants from './pages/tenants/TenantList';
import User from './pages/users/UserDetails';
import Users from './pages/users';
import AdminDashboard from './pages/dashboard/AdminDashboard';
import ChatHistoryContainer from './pages/sidekick/historyView/ChatHistoryContainer';
import ChatDetailContainer from './pages/sidekick/detailView/ChatDetailContainer';
import { PrivateRoute } from './app/PrivateRoute';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { retrieveUser } from './features/users/usersSlice';
import { ListingPage } from './pages/dashboard/ListingPage';

function App() {
    const [menuOpen, setMenuOpen] = useState(false);

    const dispatch = useDispatch();
    const oktaAuth = new OktaAuth(config.okta);

    oktaAuth.authStateManager.subscribe((authState: AuthState) => {
        localStorage.setItem('accessToken', `${authState.accessToken?.value}`);
        const userDetails: any = authState.accessToken?.value && JSON.parse(atob(authState.accessToken?.value.split('.')[1]));
        if (userDetails) {
            const isSuperAdmin = userDetails.panviva_ecosystem_claimant?.toLowerCase() === 'administrator';
            isSuperAdmin && localStorage.setItem('userRole', 'superAdmin');
            //store entire user details for future use
            localStorage.setItem('userDetails', JSON.stringify(userDetails));
            dispatch(retrieveUser(userDetails.uid)); // Fetch and store user details in Redux store
        }
    });

    const history = useHistory();
    const restoreOriginalUri = async (
        _oktaAuth: OktaAuth,
        originalUri: string
    ) => {
        history.replace(
            toRelativeUrl(originalUri || '/', window.location.origin)
        );
    };

    const handleSideNavToggle = () => {
        setMenuOpen((prev) => !prev);
    };

    useEffect(() => {
        const onUserDetailsChange = (event: StorageEvent) => {
            if (event.key === 'userDetails' && event.newValue) {
                const parsed = JSON.parse(event.newValue);
                if (parsed.uid) {
                    dispatch(retrieveUser(parsed.uid));
                }
            }
        };
        // Listen for changes to userDetails in localStorage to dispatch retrieveUser when it changes (e.g., on login)
        window.addEventListener('storage', onUserDetailsChange);
        return () => window.removeEventListener('storage', onUserDetailsChange);
    }, [dispatch, oktaAuth]);

    useEffect(() => {
        const onLogout = (event: StorageEvent) => {
            if (event.key === 'logout') {
                oktaAuth.signOut();
                history.push('/');
            }
        };
        window.addEventListener('storage', onLogout);
        return () => window.removeEventListener('storage', onLogout);
    }, [oktaAuth, history]);

    return (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            <icon-sprite />
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    height: '100vh',
                    maxHeight: '100%',
                    width: '100vw',
                    overflow: 'auto'
                }}
            >
                <Header
                    menuOpen={menuOpen}
                    setMenuOpen={() => handleSideNavToggle()}
                />
                <div
                    id="main-content"
                    className='up-h-full up-w-full'
                    style={{ display: 'flex' }}
                >
                    <div
                        style={{
                            height: '-webkit-fill-available',
                            flexGrow: 1,
                            width: '95%',
                            display: 'flex',
                            justifyContent: 'stretch',
                            alignItems: 'stretch'
                        }}
                    >
                        <Switch>
                            <Route
                                path="/login/callback"
                                component={LoginCallback}
                            />
                            <Redirect from="/" to={"/home"} exact={true} />
                            <Redirect from="/login/callback" to={"/home"} />
                            <SecureRoute path="/home" exact component={AdminDashboard} />
                            <SecureRoute
                                path="/users"
                                exact
                                component={Users}
                            />
                            <SecureRoute
                                path="/user/:userId"
                                exact
                                component={User}
                            />
                            <SecureRoute
                                path="/profile"
                                exact
                                component={Profile}
                            />
                            <SecureRoute
                                path="/organizations"
                                component={Organizations}
                            />
                            <SecureRoute
                                path="/organization/details/:organizationCode"
                                component={Organization}
                            />
                            <SecureRoute
                                path="/organization/create"
                                component={OrganizationCreate}
                            />
                            <SecureRoute
                                path="/organization/edit/:organizationCode"
                                component={OrganizationCreate}
                            />
                            <SecureRoute path="/tenants" component={Tenants} />
                            <SecureRoute
                                path="/tenant/details/:tenantCode"
                                component={Tenant}
                            />
                            <SecureRoute
                                path="/tenant/create"
                                component={TenantCreate}
                            />
                            <SecureRoute
                                path="/tenant/edit/:tenantCode"
                                component={TenantCreate}
                            />
                            <PrivateRoute
                                path="/sidekick/chat-history/:conversationId"
                                component={ChatDetailContainer}
                                requiredClaim="rag_admin_access"
                                requiredValue={true}
                            />
                            <PrivateRoute
                                path="/sidekick/chat-history"
                                component={ChatHistoryContainer}
                                requiredClaim="rag_admin_access"
                                requiredValue={true}
                            />
                            <PrivateRoute
                                path="/sidekick"
                                component={ListingPage}
                                requiredClaim="rag_admin_access"
                                requiredValue={true}
                            />
                        </Switch>
                    </div>
                </div>
            </div>
        </Security>
    );
}

export default App;
